import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import Slider from "../../../atoms/Slider/Slider";
import Cropper from "react-easy-crop";
import { Point, Area } from "react-easy-crop/types";

import "./Uploader.scss";

const Uploader = ({
  imageSource,
  setImageSource,
  setCroppedAreaPixels,
}: UploaderProps) => {
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });

  const baseStyle = {
    borderWidth: 2,
    borderRadius: "0.25em",
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    cursor: "pointer",
    height: "100%",
    width: "100%",
  };

  const focusedStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  useEffect(() => {
    return () => {
      if (imageSource as string) URL.revokeObjectURL(imageSource as string);
    };
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: {
        "image/*": [],
      },
      onDrop: (acceptedFiles) => {
        const src = URL.createObjectURL(acceptedFiles[0]);
        if (src) {
          setImageSource(src);
        }
      },
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <div className="mp-templates-profileimagemodal--main-uploader">
      <div className="main-image-container">
        {imageSource ? (
          <div className="cropped-image-container">
            <Cropper
              image={imageSource as string}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropShape="round"
            />
          </div>
        ) : (
          <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <div className="drop-container">
              <div className="drop-container-message">
                Drag images or click to upload a file
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="main-content-controls">
        {imageSource && (
          <div style={{ display: "flex", flex: 1, gap: "1em" }}>
            <div style={{ flex: 1 }}>
              <Slider
                value={zoom}
                onChange={(event) => {
                  const val = parseFloat(event.target.value);
                  setZoom(val);
                }}
                min={1}
                max={3}
                step={0.05}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Uploader;

interface UploaderProps {
  imageSource: string | null;
  setImageSource: (value: string | null) => void;
  setCroppedAreaPixels: (value: Area | null) => void;
}
